import { useEffect, useMemo, useState } from "react";

import { ApiError, demo, type DemoSlots } from "../../lib/api";
import { withUtm } from "../../lib/utm";
import { useSeo } from "../../lib/useSeo";
import { Btn } from "../../lib/Btn";
import { Input, Textarea } from "../../lib/Input";

/** Book a demo — branded slot picker backed by GET /demo/slots (Digideal's
 * shared demo-hours calendar minus busy blocks) + POST /demo/book (records the
 * booking, writes the Google Calendar event, emails both sides). Times shown
 * in the visitor's own timezone. Mirrors Casetop's feature on the same
 * calendar, so availability is coordinated across both products. */

interface DemoForm {
  name: string;
  email: string;
  company: string;
  phone: string;
  notes: string;
  website: string; // honeypot
}

const EMPTY: DemoForm = { name: "", email: "", company: "", phone: "", notes: "", website: "" };

export function BookDemoPage() {
  useSeo({
    title: "Book a live demo — NestPlot",
    description:
      "Pick a time and see NestPlot live — a focused one-hour walkthrough, your questions, real workflows: public listings, online applications and instant rent, tenant screening, e-sign leases, gate access, and rent collection.",
    path: "/book-demo",
  });

  const [slots, setSlots] = useState<DemoSlots | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [dayKey, setDayKey] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [form, setForm] = useState<DemoForm>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ slot_start: string; slot_end: string; meeting_url: string | null } | null>(null);

  const visitorTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    demo
      .slots(14)
      .then((out) => {
        setSlots(out);
        const first = (out.slots || [])[0];
        if (first) setDayKey(new Date(first).toDateString());
      })
      .catch((e) => setErr(e instanceof Error ? e.message : "Failed to load availability"));
  }, []);

  const byDay = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const iso of slots?.slots || []) {
      const key = new Date(iso).toDateString();
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(iso);
    }
    return map;
  }, [slots]);

  // Demos are a fixed one-hour slot — the server grid (slot_minutes) is the
  // single bookable length; no 30- or 90-minute options.
  const demoMinutes = slots?.slot_minutes || 60;

  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const fmtEnd = (iso: string, minutes: number) =>
    new Date(new Date(iso).getTime() + minutes * 60000).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  const fmtDay = (key: string) => {
    const d = new Date(key);
    return {
      wd: d.toLocaleDateString(undefined, { weekday: "short" }),
      md: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    };
  };

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!picked || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const out = await demo.book(
        withUtm({
          slot_start: picked,
          duration_minutes: demoMinutes,
          name: form.name,
          email: form.email,
          company: form.company || undefined,
          phone: form.phone || undefined,
          notes: form.notes || undefined,
          website: form.website || undefined,
        }) as never,
      );
      setDone(out);
    } catch (ex) {
      const msg = ex instanceof ApiError ? String((ex.payload as { detail?: string })?.detail || ex.message) : (ex as Error).message;
      if (/just booked/i.test(msg)) {
        setErr("That time was just taken — pick another slot.");
        setPicked(null);
        demo.slots(14).then(setSlots).catch(() => {});
      } else {
        setErr(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-[880px] px-5 lg:px-8 py-12 lg:py-16">
      <div className="text-center mb-8">
        <div className="font-mono text-[11px] font-bold text-accent tracking-[0.16em] uppercase mb-2.5">
          60 minutes · live walkthrough · your questions
        </div>
        <h1 className="t-display-l">Book a live demo</h1>
        <p className="text-sub text-[16px] leading-relaxed mt-3 max-w-[560px] mx-auto">
          See NestPlot on real workflows — public listings, online applications and instant rent, tenant
          screening, e-sign leases, gate access, and rent collection. Pick a time that works:
        </p>
      </div>

      {done ? (
        <div className="max-w-[520px] mx-auto bg-surface border border-accent/40 rounded-2xl p-8 text-center">
          <div className="text-[40px] mb-2">✅</div>
          <h2 className="font-display font-extrabold text-[22px] text-ink mb-2">You're booked</h2>
          <div className="text-[16px] font-bold text-accent mb-3">
            {new Date(done.slot_start).toLocaleString(undefined, {
              weekday: "long",
              month: "long",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })}
            {done.slot_end && <> – {fmtTime(done.slot_end)}</>}
            <span className="font-normal text-sub text-[13px]">
              {" "}
              ({visitorTz.split("/").pop()?.replace("_", " ")} time)
            </span>
          </div>
          <p className="text-sub text-[14px] leading-relaxed mb-4">
            A confirmation is on its way to your inbox
            {done.meeting_url ? " with the meeting link" : " — the meeting link follows by email"}. Want a
            head start? The <a href="/signup" className="text-accent">free trial</a> needs no card.
          </p>
          {done.meeting_url && (
            <a href={done.meeting_url} className="text-[14px] font-bold text-accent">
              Meeting link →
            </a>
          )}
        </div>
      ) : (
        <div className="grid gap-6 items-start lg:grid-cols-[1.2fr_1fr]">
          {/* Slot picker */}
          <div className="bg-surface border border-line rounded-2xl p-5">
            {!slots && !err && (
              <div className="text-sub text-[14px] p-6 text-center">Loading availability…</div>
            )}
            {err && !slots && (
              <div className="text-[13px] text-red-800 bg-red-50 px-3.5 py-2.5 rounded-lg">{err}</div>
            )}
            {slots && (
              <>
                <div className="flex gap-2 overflow-x-auto pb-2.5 mb-3.5 border-b border-line">
                  {[...byDay.keys()].map((key) => {
                    const { wd, md } = fmtDay(key);
                    const active = key === dayKey;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          setDayKey(key);
                          setPicked(null);
                        }}
                        className={`shrink-0 px-3.5 py-2 rounded-[10px] text-[13px] font-bold text-center border ${
                          active ? "border-accent bg-accent text-white" : "border-line bg-transparent text-ink"
                        }`}
                      >
                        <div>{wd}</div>
                        <div className="text-[11.5px] font-normal opacity-85">{md}</div>
                      </button>
                    );
                  })}
                  {byDay.size === 0 && (
                    <div className="text-[13.5px] text-sub p-2.5">
                      No open times in the next two weeks — email{" "}
                      <a href="mailto:info@nestplot.com" className="text-accent">
                        info@nestplot.com
                      </a>{" "}
                      and we'll make time.
                    </div>
                  )}
                </div>
                <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(96px,1fr))]">
                  {(byDay.get(dayKey || "") || []).map((iso) => {
                    const active = picked === iso;
                    return (
                      <button
                        key={iso}
                        type="button"
                        onClick={() => setPicked(iso)}
                        className={`py-2.5 rounded-lg font-mono text-[13.5px] border ${
                          active
                            ? "border-accent bg-accent text-white font-bold"
                            : "border-line bg-surface text-ink font-normal"
                        }`}
                      >
                        {fmtTime(iso)}
                      </button>
                    );
                  })}
                </div>
                <div className="text-[11.5px] text-sub/80 mt-3.5">
                  Times shown in your timezone ({visitorTz.replace("_", " ")}) · each demo is a focused
                  one-hour walkthrough.
                </div>
              </>
            )}
          </div>

          {/* Details form */}
          <form onSubmit={onSubmit} className="bg-surface border border-line rounded-2xl p-5 flex flex-col gap-3">
            <div className="text-[15px] font-extrabold text-ink">
              {picked
                ? `${new Date(picked).toLocaleString(undefined, {
                    weekday: "long",
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })} – ${fmtEnd(picked, demoMinutes)} · ${demoMinutes} min`
                : "Pick a time, then tell us who you are"}
            </div>
            <Input
              required
              placeholder="Your name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <Input
              required
              type="email"
              placeholder="Work email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <Input
              placeholder="Company (optional)"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
            />
            <Input
              placeholder="Phone (optional)"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
            <Textarea
              rows={3}
              placeholder="Anything specific you want to see? (optional)"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
            {/* Honeypot — hidden from humans, tempting for bots */}
            <input
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="Website"
              className="absolute -left-[9999px] w-px h-px opacity-0"
            />
            {err && slots && (
              <div className="text-[13px] text-red-800 bg-red-50 px-3 py-2 rounded-lg">{err}</div>
            )}
            <Btn type="submit" size="lg" full disabled={!picked || busy}>
              {busy ? "Booking…" : "Confirm demo →"}
            </Btn>
            <div className="text-[11.5px] text-sub/80 leading-snug">
              We'll send a confirmation and a calendar invite. No sales pressure — it's a walkthrough of the
              product with our team.
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
